import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useQuery, useMutation } from "convex/react";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { format } from "date-fns";
import { Link, useRoute, useLocation } from "wouter";
import {
  Stethoscope,
  UserRound,
  LogOut,
  Plus,
  FileText,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Loader2,
  Trash2,
  X,
  ArrowLeft,
  LayoutDashboard,
  MessageSquare,
  Copy,
  Pencil,
  MoreVertical,
  Sparkles,
  Globe,
  Eye,
  ShieldCheck,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Settings as SettingsIcon,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormatTemplateEditor from "@/components/FormatTemplateEditor";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";
import { EditHistoryPanel, LastEditedLabel } from "@/components/EditHistoryPanel";
import { History } from "lucide-react";

type Template = {
  _id: Id<"templates">;
  userId: Id<"users">;
  name: string;
  description?: string;
  order?: number;
  profileId?: Id<"profiles">;
  createdAt: number;
  updatedAt: number;
};

type TemplateSection = {
  _id: Id<"templateSections">;
  templateId: Id<"templates">;
  name: string;
  description: string;
  order: number;
  examples: string[];
  adhereToFormatting?: boolean;
  formatTemplate?: string;
  doubleSpaceOutput?: boolean;
  allowAssessment?: boolean;
  tagIds?: Id<"ruleTags">[];
  relatedSectionIds?: Id<"templateSections">[];
};

type RuleTag = {
  _id: Id<"ruleTags">;
  name: string;
  normalizedName: string;
};

function NewTemplateDialog({
  userId,
  onSuccess,
}: {
  userId: Id<"users">;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const { toast } = useToast();

  const createTemplate = useMutation(api.templates.create);

  const resetForm = () => {
    setName("");
    setDescription("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createTemplate({
        userId,
        name,
        description: description || undefined,
      });
      toast({
        title: "Template created",
        description: "Your template has been created with a default Title section.",
      });
      setOpen(false);
      resetForm();
      onSuccess();
    } catch {
      toast({
        title: "Error",
        description: "Failed to create template.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateWithAi = async () => {
    if (!description.trim()) {
      toast({
        title: "Description required",
        description: "Please describe the template so the AI knows what to build.",
        variant: "destructive",
      });
      return;
    }
    setAiLoading(true);
    try {
      const res = await fetch("/api/templates/generate-ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, description }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "AI generation failed");
      }
      const data = await res.json();
      toast({
        title: "Template generated",
        description: `Created ${data.sectionCount} AI-generated sections. Review and edit as needed.`,
      });
      setOpen(false);
      resetForm();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "AI generation failed",
        description: error?.message || "Could not generate template.",
        variant: "destructive",
      });
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!aiLoading) setOpen(o); }}>
      <DialogTrigger asChild>
        <Button className="w-full gap-2" data-testid="button-new-template">
          <Plus className="w-4 h-4" />
          New Template
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Template</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="templateName">Template Name</Label>
            <Input
              id="templateName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Clinical Note Template"
              required
              data-testid="input-template-name"
              disabled={aiLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="templateDescription">
              Description{" "}
              <span className="text-xs text-muted-foreground font-normal">
                (required for AI generation)
              </span>
            </Label>
            <Textarea
              id="templateDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this template (e.g. 'SOAP note for primary care visits including vitals, HPI, ROS, exam, assessment, and plan')..."
              data-testid="input-template-description"
              disabled={aiLoading}
              rows={4}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={handleGenerateWithAi}
              disabled={aiLoading || !description.trim()}
              data-testid="button-generate-template-ai"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating sections with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate with AI
                </>
              )}
            </Button>
            <Button
              type="submit"
              className="w-full"
              data-testid="button-create-template"
              disabled={aiLoading}
            >
              Create Empty Template
            </Button>
            {aiLoading && (
              <p className="text-xs text-muted-foreground text-center">
                This usually takes 20-60 seconds. Please don't close this window.
              </p>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type SectionUpdates = {
  name: string;
  description: string;
  examples: string[];
  adhereToFormatting?: boolean;
  formatTemplate?: string;
  doubleSpaceOutput?: boolean;
  allowAssessment?: boolean;
  tagIds?: Id<"ruleTags">[];
  relatedSectionIds?: Id<"templateSections">[];
};

type ConflictCheckPayload = {
  sectionName?: string;
  description: string;
  examples: string[];
  adhereToFormatting: boolean;
  formatTemplate?: string;
  taggedRules: { tagName: string; content: string }[];
};

type ConflictCacheEntry = {
  hash: string;
  result: { hasConflicts: boolean; conflicts: { between: string[]; explanation: string }[] };
};

function hashConflictPayload(payload: ConflictCheckPayload): string {
  return JSON.stringify({
    sectionName: payload.sectionName ?? "",
    description: payload.description ?? "",
    examples: payload.examples ?? [],
    adhereToFormatting: payload.adhereToFormatting ?? false,
    formatTemplate: payload.adhereToFormatting ? (payload.formatTemplate ?? "") : "",
    taggedRules: (payload.taggedRules ?? [])
      .map((r) => ({ tagName: r.tagName, content: r.content }))
      .sort((a, b) =>
        a.tagName === b.tagName
          ? a.content.localeCompare(b.content)
          : a.tagName.localeCompare(b.tagName),
      ),
  });
}

function SectionEditor({
  section,
  userId,
  onUpdate,
  onDelete,
  canDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  showReorder,
  tags,
  rules,
  siblingSections,
  getCachedConflict,
  setCachedConflict,
}: {
  section: TemplateSection;
  userId: Id<"users">;
  onUpdate: (updates: SectionUpdates) => void | Promise<void>;
  onDelete: () => void;
  canDelete: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  showReorder: boolean;
  tags: RuleTag[];
  rules: Array<{
    _id: Id<"globalRules">;
    content: string;
    tagId?: Id<"ruleTags">;
  }>;
  siblingSections: TemplateSection[];
  getCachedConflict: (sectionId: string, hash: string) => ConflictCacheEntry | undefined;
  setCachedConflict: (sectionId: string, entry: ConflictCacheEntry) => void;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [name, setName] = useState(section.name);
  const [description, setDescription] = useState(section.description);
  const [examples, setExamples] = useState<string[]>(section.examples);
  const [adhereToFormatting, setAdhereToFormatting] = useState(section.adhereToFormatting ?? false);
  const [formatTemplate, setFormatTemplate] = useState(section.formatTemplate ?? "");
  const [doubleSpaceOutput, setDoubleSpaceOutput] = useState(section.doubleSpaceOutput ?? false);
  const [allowAssessment, setAllowAssessment] = useState(section.allowAssessment ?? false);
  const [tagIds, setTagIds] = useState<Id<"ruleTags">[]>(section.tagIds || []);
  const [relatedSectionIds, setRelatedSectionIds] = useState<Id<"templateSections">[]>(
    section.relatedSectionIds || [],
  );
  const [newExample, setNewExample] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"description" | "examples" | "formatting">("description");
  const { toast } = useToast();

  const tagById = new Map(tags.map((t) => [String(t._id), t]));

  type SuggestedFix =
    | {
        target: "description" | "formatTemplate";
        action: "replace";
        newValue: string;
        rationale?: string;
      }
    | {
        target: "example";
        action: "replace";
        exampleIndex: number;
        newValue: string;
        rationale?: string;
      }
    | {
        target: "example";
        action: "remove";
        exampleIndex: number;
        rationale?: string;
      };

  type ConflictResult = {
    hasConflicts: boolean;
    conflicts: {
      between: string[];
      explanation: string;
      suggestedFix?: SuggestedFix | null;
    }[];
  };

  const buildConflictPayload = (): ConflictCheckPayload => {
    const selectedTagIds = new Set(tagIds.map((id) => String(id)));
    const taggedRules = rules
      .filter((r) => r.tagId && selectedTagIds.has(String(r.tagId)))
      .map((r) => {
        const tagName = (r.tagId && tagById.get(String(r.tagId))?.name) || "Untagged";
        return { tagName, content: r.content };
      });
    return {
      sectionName: name,
      description,
      examples,
      adhereToFormatting,
      formatTemplate: adhereToFormatting ? formatTemplate : undefined,
      taggedRules,
    };
  };

  const conflictMutation = useReactQueryMutation<ConflictResult>({
    mutationFn: async () => {
      const payload = buildConflictPayload();
      const hash = hashConflictPayload(payload);
      const res = await apiRequest("POST", "/api/templates/sections/check-conflicts", payload);
      const data = (await res.json()) as ConflictResult;
      setCachedConflict(String(section._id), { hash, result: data });
      return data;
    },
    onError: (err: any) => {
      setConflictOpen(false);
      toast({
        title: "Conflict check failed",
        description: err?.message || "Could not analyze this section.",
        variant: "destructive",
      });
    },
  });

  const conflictLoading = conflictMutation.isPending;
  const currentConflictHash = hashConflictPayload(buildConflictPayload());
  const cachedConflict = getCachedConflict(String(section._id), currentConflictHash);
  const conflictResult: ConflictResult | null =
    conflictMutation.data ?? cachedConflict?.result ?? null;

  const runConflictCheck = (force = false) => {
    setLastAppliedFix(null);
    setConflictOpen(true);
    if (!force) {
      const cached = getCachedConflict(String(section._id), currentConflictHash);
      if (cached) {
        conflictMutation.reset();
        return;
      }
    }
    conflictMutation.reset();
    conflictMutation.mutate();
  };

  const [applyingFixIndex, setApplyingFixIndex] = useState<number | null>(null);
  const [undoingFix, setUndoingFix] = useState(false);
  const [lastAppliedFix, setLastAppliedFix] = useState<{
    targetLabel: string;
    prevDescription: string;
    prevExamples: string[];
    prevFormatTemplate: string;
  } | null>(null);

  const applySuggestedFix = async (conflictIndex: number, fix: SuggestedFix) => {
    const prevDescription = description;
    const prevExamples = examples;
    const prevFormatTemplate = formatTemplate;

    let nextDescription = description;
    let nextExamples = examples;
    let nextFormatTemplate = formatTemplate;
    let targetLabel = "";

    if (fix.target === "description") {
      nextDescription = fix.newValue;
      setDescription(fix.newValue);
      targetLabel = "description";
    } else if (fix.target === "formatTemplate") {
      nextFormatTemplate = fix.newValue;
      setFormatTemplate(fix.newValue);
      targetLabel = "format template";
    } else if (fix.target === "example") {
      const idx = fix.exampleIndex - 1;
      if (idx < 0 || idx >= examples.length) {
        toast({
          title: "Could not apply fix",
          description: `Example ${fix.exampleIndex} no longer exists.`,
          variant: "destructive",
        });
        return;
      }
      if (fix.action === "remove") {
        nextExamples = examples.filter((_, i) => i !== idx);
        targetLabel = `example ${fix.exampleIndex}`;
      } else {
        nextExamples = examples.map((e, i) => (i === idx ? fix.newValue : e));
        targetLabel = `example ${fix.exampleIndex}`;
      }
      setExamples(nextExamples);
    }

    setApplyingFixIndex(conflictIndex);
    setLastAppliedFix(null);
    try {
      await onUpdate({
        name,
        description: nextDescription,
        examples: nextExamples,
        adhereToFormatting,
        formatTemplate: adhereToFormatting ? nextFormatTemplate : undefined,
        doubleSpaceOutput,
        allowAssessment,
        tagIds,
      });
      setLastAppliedFix({
        targetLabel,
        prevDescription,
        prevExamples,
        prevFormatTemplate,
      });
    } catch {
      setDescription(prevDescription);
      setExamples(prevExamples);
      setFormatTemplate(prevFormatTemplate);
    } finally {
      setApplyingFixIndex(null);
    }
  };

  const undoAppliedFix = async () => {
    if (!lastAppliedFix) return;
    const { prevDescription, prevExamples, prevFormatTemplate } = lastAppliedFix;
    const beforeDescription = description;
    const beforeExamples = examples;
    const beforeFormatTemplate = formatTemplate;
    setDescription(prevDescription);
    setExamples(prevExamples);
    setFormatTemplate(prevFormatTemplate);
    setUndoingFix(true);
    try {
      await onUpdate({
        name,
        description: prevDescription,
        examples: prevExamples,
        adhereToFormatting,
        formatTemplate: adhereToFormatting ? prevFormatTemplate : undefined,
        doubleSpaceOutput,
        allowAssessment,
        tagIds,
      });
      setLastAppliedFix(null);
      conflictMutation.reset();
      setConflictOpen(false);
    } catch {
      setDescription(beforeDescription);
      setExamples(beforeExamples);
      setFormatTemplate(beforeFormatTemplate);
    } finally {
      setUndoingFix(false);
    }
  };

  const handleSave = () => {
    onUpdate({ name, description, examples, adhereToFormatting, formatTemplate: adhereToFormatting ? formatTemplate : undefined, doubleSpaceOutput, allowAssessment, tagIds, relatedSectionIds });
    setIsEditing(false);
  };

  const toggleRelatedSection = (relatedId: Id<"templateSections">) => {
    const idStr = String(relatedId);
    const already = relatedSectionIds.some((r) => String(r) === idStr);
    const next = already
      ? relatedSectionIds.filter((r) => String(r) !== idStr)
      : [...relatedSectionIds, relatedId];
    setRelatedSectionIds(next);
    if (!isEditing) {
      onUpdate({
        name: section.name,
        description: section.description,
        examples: section.examples,
        adhereToFormatting,
        formatTemplate: adhereToFormatting ? (formatTemplate || section.formatTemplate) : undefined,
        doubleSpaceOutput,
        allowAssessment,
        tagIds,
        relatedSectionIds: next,
      });
    }
  };

  const toggleTag = (tagId: Id<"ruleTags">) => {
    const idStr = String(tagId);
    const already = tagIds.some((t) => String(t) === idStr);
    const next = already
      ? tagIds.filter((t) => String(t) !== idStr)
      : [...tagIds, tagId];
    setTagIds(next);
    if (!isEditing) {
      onUpdate({
        name: section.name,
        description: section.description,
        examples: section.examples,
        adhereToFormatting,
        formatTemplate: adhereToFormatting ? (formatTemplate || section.formatTemplate) : undefined,
        doubleSpaceOutput,
        allowAssessment,
        tagIds: next,
      });
    }
  };

  const addExample = () => {
    if (newExample.trim()) {
      const updated = [...examples, newExample.trim()];
      setExamples(updated);
      setNewExample("");
    }
  };

  const removeExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index));
  };

  return (
    <Card className="mb-3">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {showReorder && (
            <div className="flex flex-col gap-0.5">
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5"
                disabled={isFirst}
                onClick={onMoveUp}
                data-testid={`button-move-up-${section._id}`}
              >
                <ChevronUp className="w-3 h-3" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5"
                disabled={isLast}
                onClick={onMoveDown}
                data-testid={`button-move-down-${section._id}`}
              >
                <ChevronDown className="w-3 h-3" />
              </Button>
            </div>
          )}
          <div className="flex-1">
            {isEditing ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="font-semibold"
                data-testid={`input-section-name-${section._id}`}
              />
            ) : (
              <>
                <CardTitle className="text-base">{section.name}</CardTitle>
                <div className="mt-0.5">
                  <LastEditedLabel
                    userId={userId}
                    entityType="templateSection"
                    entityId={String(section._id)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isEditing ? (
              <>
                <Button size="sm" onClick={handleSave} data-testid={`button-save-section-${section._id}`}>
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setName(section.name);
                    setDescription(section.description);
                    setExamples(section.examples);
                    setAdhereToFormatting(section.adhereToFormatting ?? false);
                    setFormatTemplate(section.formatTemplate ?? "");
                    setDoubleSpaceOutput(section.doubleSpaceOutput ?? false);
                    setAllowAssessment(section.allowAssessment ?? false);
                    setTagIds(section.tagIds || []);
                    setRelatedSectionIds(section.relatedSectionIds || []);
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowPromptPreview(true)}
                  className="h-8 w-8"
                  title="Preview full LLM prompt for this section"
                  data-testid={`button-preview-prompt-${section._id}`}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Popover
                  open={conflictOpen}
                  onOpenChange={(open) => {
                    setConflictOpen(open);
                    if (!open) setLastAppliedFix(null);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!conflictLoading) runConflictCheck();
                      }}
                      disabled={conflictLoading}
                      className="h-8 w-8"
                      title="Check this section for conflicts between description, examples, format, and tagged rules"
                      data-testid={`button-check-conflicts-${section._id}`}
                    >
                      {conflictLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-4 h-4" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-96"
                    data-testid={`popover-conflicts-${section._id}`}
                  >
                    {!conflictLoading && conflictResult && (
                      <div className="flex justify-end mb-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => runConflictCheck(true)}
                          data-testid={`button-rerun-conflicts-${section._id}`}
                        >
                          Re-run
                        </Button>
                      </div>
                    )}
                    {conflictLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analyzing section for conflicts…
                      </div>
                    ) : conflictResult ? (
                      conflictResult.hasConflicts && conflictResult.conflicts.length > 0 ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="w-4 h-4" />
                            {conflictResult.conflicts.length} potential conflict
                            {conflictResult.conflicts.length === 1 ? "" : "s"} found
                          </div>
                          {lastAppliedFix && (
                            <div
                              className="flex items-center justify-between gap-2 text-xs border border-border rounded p-2 bg-muted/30"
                              data-testid={`fix-undo-banner-${section._id}`}
                            >
                              <span className="text-muted-foreground">
                                Applied fix to {lastAppliedFix.targetLabel}.
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={undoingFix}
                                onClick={undoAppliedFix}
                                data-testid={`button-undo-fix-${section._id}`}
                              >
                                {undoingFix ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Undoing…
                                  </>
                                ) : (
                                  "Undo"
                                )}
                              </Button>
                            </div>
                          )}
                          <ul className="space-y-3 max-h-80 overflow-auto">
                            {conflictResult.conflicts.map((c, i) => {
                              const fix = c.suggestedFix ?? null;
                              let fixTargetLabel = "";
                              if (fix) {
                                if (fix.target === "example") {
                                  fixTargetLabel = `example ${fix.exampleIndex}`;
                                } else if (fix.target === "formatTemplate") {
                                  fixTargetLabel = "format template";
                                } else {
                                  fixTargetLabel = "description";
                                }
                              }
                              return (
                                <li
                                  key={i}
                                  className="text-sm border border-border rounded p-2 bg-muted/30"
                                  data-testid={`conflict-item-${section._id}-${i}`}
                                >
                                  {c.between && c.between.length > 0 && (
                                    <div className="text-xs text-muted-foreground mb-1">
                                      Between: {c.between.join(" ↔ ")}
                                    </div>
                                  )}
                                  <div className="whitespace-pre-wrap">{c.explanation}</div>
                                  {fix ? (
                                    <div
                                      className="mt-2 border-t border-border pt-2 space-y-2"
                                      data-testid={`conflict-fix-${section._id}-${i}`}
                                    >
                                      <div className="text-xs font-medium text-foreground">
                                        Suggested fix:{" "}
                                        <span className="font-normal text-muted-foreground">
                                          {fix.action === "remove"
                                            ? `Remove ${fixTargetLabel}`
                                            : `Update ${fixTargetLabel}`}
                                        </span>
                                      </div>
                                      {fix.rationale && (
                                        <div className="text-xs text-muted-foreground italic">
                                          {fix.rationale}
                                        </div>
                                      )}
                                      {fix.action === "replace" && (
                                        <div
                                          className="text-xs whitespace-pre-wrap bg-background border border-border rounded p-2 max-h-32 overflow-auto"
                                          data-testid={`conflict-fix-preview-${section._id}-${i}`}
                                        >
                                          {fix.newValue}
                                        </div>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        disabled={applyingFixIndex !== null}
                                        onClick={() => applySuggestedFix(i, fix)}
                                        data-testid={`button-apply-fix-${section._id}-${i}`}
                                      >
                                        {applyingFixIndex === i ? (
                                          <>
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Applying…
                                          </>
                                        ) : (
                                          "Apply fix"
                                        )}
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground italic">
                                      No automatic fix available — resolve manually.
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="w-4 h-4" />
                          No conflicts found.
                        </div>
                      )
                    ) : (
                      <div className="text-sm text-muted-foreground">No result yet.</div>
                    )}
                  </PopoverContent>
                </Popover>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setHistoryOpen(true)}
                  className="h-8 w-8"
                  title="View edit history for this section"
                  data-testid={`button-section-history-${section._id}`}
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  data-testid={`button-edit-section-${section._id}`}
                >
                  Edit
                </Button>
              </>
            )}
            {canDelete && (
              <Button
                size="icon"
                variant="ghost"
                onClick={onDelete}
                className="text-destructive hover:text-destructive"
                data-testid={`button-delete-section-${section._id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "description" | "examples" | "formatting")}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="description" data-testid={`tab-description-${section._id}`}>
            Description
          </TabsTrigger>
          <TabsTrigger value="examples" data-testid={`tab-examples-${section._id}`}>
            Examples
          </TabsTrigger>
          <TabsTrigger value="formatting" data-testid={`tab-formatting-${section._id}`}>
            Formatting
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="description"
          className="space-y-4 mt-0"
          data-testid={`tab-panel-description-${section._id}`}
        >
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">
            LLM Prompt (Description)
          </Label>
          {isEditing ? (
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this section should contain..."
              className="text-sm min-h-[24rem] resize-y"
              data-testid={`input-section-description-${section._id}`}
            />
          ) : (
            <p className="text-sm text-foreground bg-muted/50 p-2 rounded whitespace-pre-wrap">
              {section.description}
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs text-muted-foreground mb-2 block">
            Tags ({tagIds.length})
          </Label>
          {tags.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No tags exist yet. Create tags on the Rules page to associate rules with this section.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const selected = tagIds.some((id) => String(id) === String(t._id));
                return (
                  <button
                    key={t._id}
                    type="button"
                    onClick={() => toggleTag(t._id)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      selected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/30 text-muted-foreground border-border hover:bg-muted"
                    }`}
                    data-testid={`button-toggle-tag-${section._id}-${t._id}`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          )}
          {tagIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Rules tagged with the selected tags will be included when this
              section is generated and verified.
            </p>
          )}
          {/* avoid unused warning */}
          <span className="hidden">{tagById.size}</span>
        </div>

        <div data-testid={`related-sections-control-${section._id}`}>
          <Label className="text-xs text-muted-foreground mb-2 block">
            Related sections — Don't repeat from ({relatedSectionIds.length})
          </Label>
          {siblingSections.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No other sections in this template yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {siblingSections.map((sib) => {
                const selected = relatedSectionIds.some(
                  (id) => String(id) === String(sib._id),
                );
                return (
                  <button
                    key={sib._id}
                    type="button"
                    onClick={() => toggleRelatedSection(sib._id)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      selected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/30 text-muted-foreground border-border hover:bg-muted"
                    }`}
                    data-testid={`button-toggle-related-section-${section._id}-${sib._id}`}
                  >
                    {sib.name}
                  </button>
                );
              })}
            </div>
          )}
          {relatedSectionIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              When generating, this section will be told not to repeat content already
              covered by the selected sections.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`allow-assessment-${section._id}`}
            checked={allowAssessment}
            onChange={(e) => {
              const checked = e.target.checked;
              setAllowAssessment(checked);
              if (!isEditing) {
                onUpdate({
                  name: section.name,
                  description: section.description,
                  examples: section.examples,
                  adhereToFormatting,
                  formatTemplate: adhereToFormatting ? (formatTemplate || section.formatTemplate) : undefined,
                  doubleSpaceOutput,
                  allowAssessment: checked,
                });
              }
            }}
            className="h-4 w-4 rounded border-border"
            data-testid={`checkbox-allow-assessment-${section._id}`}
          />
          <Label
            htmlFor={`allow-assessment-${section._id}`}
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            Allow assessment
          </Label>
        </div>
        </TabsContent>

        <TabsContent
          value="examples"
          className="space-y-4 mt-0"
          data-testid={`tab-panel-examples-${section._id}`}
        >
        <div>
          <Label className="text-xs text-muted-foreground mb-2 block">
            Examples ({examples.length})
          </Label>
          <div className="space-y-2">
            {examples.map((example, index) => (
              <div
                key={index}
                className="flex items-start gap-2 bg-muted/30 p-2 rounded text-sm"
              >
                {isEditing ? (
                  <Textarea
                    value={example}
                    onChange={(e) => {
                      const updated = [...examples];
                      updated[index] = e.target.value;
                      setExamples(updated);
                    }}
                    className="flex-1 text-sm min-h-[60px]"
                    data-testid={`input-edit-example-${section._id}-${index}`}
                  />
                ) : (
                  <span className="flex-1 whitespace-pre-wrap">{example}</span>
                )}
                {isEditing && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0"
                    onClick={() => removeExample(index)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
            {isEditing && (
              <div className="space-y-2">
                <Textarea
                  value={newExample}
                  onChange={(e) => setNewExample(e.target.value)}
                  placeholder="Add an example..."
                  className="text-sm min-h-[60px]"
                  data-testid={`input-new-example-${section._id}`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addExample}
                  data-testid={`button-add-example-${section._id}`}
                >
                  Add Example
                </Button>
              </div>
            )}
          </div>
        </div>
        </TabsContent>

        <TabsContent
          value="formatting"
          className="space-y-4 mt-0"
          data-testid={`tab-panel-formatting-${section._id}`}
        >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`adhere-formatting-${section._id}`}
              checked={adhereToFormatting}
              onChange={(e) => {
                const checked = e.target.checked;
                setAdhereToFormatting(checked);
                if (!isEditing) {
                  onUpdate({
                    name: section.name,
                    description: section.description,
                    examples: section.examples,
                    adhereToFormatting: checked,
                    formatTemplate: checked ? (formatTemplate || section.formatTemplate) : undefined,
                    allowAssessment,
                  });
                }
              }}
              className="h-4 w-4 rounded border-border"
              data-testid={`checkbox-adhere-formatting-${section._id}`}
            />
            <Label
              htmlFor={`adhere-formatting-${section._id}`}
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Adhere to a specific output format
            </Label>
          </div>

          {adhereToFormatting && (
            <div className="space-y-3 pl-6" data-testid={`format-controls-${section._id}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`double-space-${section._id}`}
                  checked={doubleSpaceOutput}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setDoubleSpaceOutput(checked);
                    if (!isEditing) {
                      onUpdate({
                        name: section.name,
                        description: section.description,
                        examples: section.examples,
                        adhereToFormatting,
                        formatTemplate: formatTemplate || section.formatTemplate,
                        doubleSpaceOutput: checked,
                        allowAssessment,
                      });
                    }
                  }}
                  className="h-4 w-4 rounded border-border"
                  data-testid={`checkbox-double-space-${section._id}`}
                />
                <Label
                  htmlFor={`double-space-${section._id}`}
                  className="text-sm text-muted-foreground cursor-pointer select-none"
                >
                  Add blank line between each output line
                </Label>
              </div>
              <Label className="text-xs text-muted-foreground block">
                Format Template
              </Label>
              <FormatTemplateEditor
                value={formatTemplate}
                onChange={(md) => setFormatTemplate(md)}
                readOnly={!isEditing}
                testIdPrefix={`format-editor-${section._id}`}
              />
              <p className="text-xs text-muted-foreground">
                Design the output format visually. Use the Placeholder button to insert dynamic fields (e.g. {"{{"}chief_complaint{"}}"}). The AI will fill in the placeholders with patient data.
              </p>
            </div>
          )}
        </div>
        </TabsContent>
      </Tabs>
      </CardContent>
      <PromptPreviewDialog
        open={showPromptPreview}
        onOpenChange={setShowPromptPreview}
        sectionName={section.name}
        description={description}
        examples={examples}
        adhereToFormatting={adhereToFormatting}
        formatTemplate={formatTemplate}
        doubleSpaceOutput={doubleSpaceOutput}
        allowAssessment={allowAssessment}
        tagIds={tagIds}
        tags={tags}
        rules={rules}
      />
      <EditHistoryPanel
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        userId={userId}
        entityType="templateSection"
        entityIds={[String(section._id)]}
        title={`Edit history — ${section.name}`}
        description="Track every change to this section's name, description, examples, and format template."
        restoreSection={{
          sectionId: section._id,
          currentName: section.name,
          currentDescription: section.description,
          currentExamples: examples,
          currentFormatTemplate: formatTemplate ?? "",
        }}
      />
    </Card>
  );
}

function PromptPreviewDialog({
  open,
  onOpenChange,
  sectionName,
  description,
  examples,
  adhereToFormatting,
  formatTemplate,
  doubleSpaceOutput,
  allowAssessment,
  tagIds,
  tags,
  rules,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sectionName: string;
  description: string;
  examples: string[];
  adhereToFormatting: boolean;
  formatTemplate: string;
  doubleSpaceOutput: boolean;
  allowAssessment: boolean;
  tagIds: Id<"ruleTags">[];
  tags: RuleTag[];
  rules: Array<{
    _id: Id<"globalRules">;
    content: string;
    tagId?: Id<"ruleTags">;
  }>;
}) {
  const { toast } = useToast();
  const tagById = new Map(tags.map((t) => [String(t._id), t]));
  const tagIdStrSet = new Set(tagIds.map((t) => String(t)));
  const matchingRules = rules.filter(
    (r) => r.tagId && tagIdStrSet.has(String(r.tagId)),
  );

  const grouped = new Map<string, string[]>();
  for (const rule of matchingRules) {
    const tag = tagById.get(String(rule.tagId));
    const tagName = tag?.name || "Untagged";
    if (!grouped.has(tagName)) grouped.set(tagName, []);
    grouped.get(tagName)!.push(rule.content);
  }
  const groupedTagNames = Array.from(grouped.keys()).sort((a, b) => {
    if (a.toLowerCase() === "global") return -1;
    if (b.toLowerCase() === "global") return 1;
    return a.localeCompare(b);
  });

  const promptParts: string[] = [];

  promptParts.push(`SECTION: ${sectionName}`);
  promptParts.push("");

  promptParts.push("TRANSCRIPTION:");
  promptParts.push("<the recorded transcription will be inserted here at runtime>");
  promptParts.push("");

  promptParts.push("CLINICAL NOTES:");
  promptParts.push("<any additional clinical notes will be inserted here at runtime>");
  promptParts.push("");

  if (matchingRules.length > 0) {
    promptParts.push(
      "TAG-SCOPED RULES (these apply to this section because the section is tagged accordingly, and OVERRIDE conflicting section-level guidance):",
    );
    for (const tagName of groupedTagNames) {
      promptParts.push(`Tag "${tagName}":`);
      grouped.get(tagName)!.forEach((c, i) => {
        promptParts.push(`  ${i + 1}. ${c}`);
      });
    }
    promptParts.push("");
  } else if (tagIds.length === 0) {
    promptParts.push(
      "TAG-SCOPED RULES: <none — this section has no tags assigned, so no rules will be applied>",
    );
    promptParts.push("");
  } else {
    promptParts.push(
      "TAG-SCOPED RULES: <none — no rules exist for the tags assigned to this section>",
    );
    promptParts.push("");
  }

  promptParts.push("SECTION DESCRIPTION (LLM prompt for this section):");
  promptParts.push(description || "<empty>");
  promptParts.push("");

  if (examples.length > 0) {
    promptParts.push(`SECTION EXAMPLES (${examples.length}):`);
    examples.forEach((ex, i) => {
      promptParts.push(`Example ${i + 1}:`);
      promptParts.push(ex);
      promptParts.push("");
    });
  } else {
    promptParts.push("SECTION EXAMPLES: <none>");
    promptParts.push("");
  }

  if (adhereToFormatting) {
    promptParts.push("FORMAT TEMPLATE (the model must adhere to this format):");
    promptParts.push(formatTemplate || "<empty>");
    promptParts.push("");
  }

  const flags: string[] = [];
  if (doubleSpaceOutput) flags.push("Double-space output");
  if (allowAssessment) flags.push("Allow assessment");
  if (flags.length > 0) {
    promptParts.push(`OPTIONS: ${flags.join(", ")}`);
  }

  const fullPrompt = promptParts.join("\n");

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(fullPrompt);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            LLM Prompt Preview — {sectionName}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">
          This is what will be sent to the model when generating or verifying
          this section. Transcription and clinical notes are inserted at
          runtime.
        </div>
        <ScrollArea className="flex-1 border border-border rounded-md bg-muted/30">
          <pre
            className="p-4 text-xs font-mono whitespace-pre-wrap text-foreground"
            data-testid="text-prompt-preview"
          >
            {fullPrompt}
          </pre>
        </ScrollArea>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyPrompt}
            data-testid="button-copy-prompt"
          >
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </Button>
          <Button
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-prompt-preview"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type TemplateConflictSummary = {
  checkedAt: number;
  totalSections: number;
  sectionsWithConflicts: number;
  conflictCount: number;
  errorCount: number;
};

function TemplateEditor({
  template,
  userId,
  onBack,
  onConflictCheckComplete,
}: {
  template: Template;
  userId: Id<"users">;
  onBack: () => void;
  onConflictCheckComplete?: (
    templateId: Id<"templates">,
    summary: TemplateConflictSummary,
  ) => void;
}) {
  const { toast } = useToast();

  const sections = useQuery(api.templates.getSections, {
    templateId: template._id,
    userId,
  });

  const tags = useQuery(api.ruleTags.list, { userId });
  const rules = useQuery(api.globalRules.list, { userId });
  const profiles = useQuery(api.profiles.list, { userId });

  const addSectionMutation = useMutation(api.templates.addSection);
  const updateSectionMutation = useMutation(api.templates.updateSection);
  const deleteSectionMutation = useMutation(api.templates.deleteSection);
  const reorderSectionsMutation = useMutation(api.templates.reorderSections);
  const updateTemplateMutation = useMutation(api.templates.update);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(template.name);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionDesc, setNewSectionDesc] = useState("");
  const [newSectionExamples, setNewSectionExamples] = useState<string[]>([]);
  const [newExampleText, setNewExampleText] = useState("");
  const [showAddSection, setShowAddSection] = useState(false);
  const lastSectionRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [shouldScrollToLast, setShouldScrollToLast] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  type SectionConflictResult = {
    hasConflicts: boolean;
    conflicts: { between: string[]; explanation: string }[];
  };
  type TemplateConflictEntry = {
    sectionId: string;
    sectionName: string;
    status: "pending" | "ok" | "conflict" | "error";
    result?: SectionConflictResult;
    error?: string;
  };
  const [templateCheckOpen, setTemplateCheckOpen] = useState(false);
  const [templateCheckRunning, setTemplateCheckRunning] = useState(false);
  const [templateCheckResults, setTemplateCheckResults] = useState<TemplateConflictEntry[]>([]);
  const [conflictCache, setConflictCache] = useState<Record<string, ConflictCacheEntry>>({});

  const persistedConflictResults = useQuery(api.sectionConflictResults.listForTemplate, {
    userId,
    templateId: template._id,
  });
  const upsertConflictResult = useMutation(api.sectionConflictResults.upsert);

  useEffect(() => {
    if (!persistedConflictResults) return;
    setConflictCache((prev) => {
      const next: Record<string, ConflictCacheEntry> = { ...prev };
      for (const row of persistedConflictResults) {
        const key = String(row.sectionId);
        const existing = next[key];
        if (existing && existing.hash === row.hash) continue;
        try {
          const parsed = JSON.parse(row.resultJson) as ConflictCacheEntry["result"];
          next[key] = { hash: row.hash, result: parsed };
        } catch {
          // ignore malformed entries
        }
      }
      return next;
    });
  }, [persistedConflictResults]);

  const getCachedConflict = (sectionId: string, hash: string): ConflictCacheEntry | undefined => {
    const entry = conflictCache[sectionId];
    return entry && entry.hash === hash ? entry : undefined;
  };

  const setCachedConflict = (sectionId: string, entry: ConflictCacheEntry) => {
    setConflictCache((prev) => ({ ...prev, [sectionId]: entry }));
    upsertConflictResult({
      userId,
      sectionId: sectionId as Id<"templateSections">,
      hash: entry.hash,
      resultJson: JSON.stringify(entry.result),
    }).catch(() => {
      // best-effort persistence; in-memory cache still works
    });
  };

  const runTemplateConflictCheck = async (force = false) => {
    if (!sections || sections.length === 0 || !rules) {
      toast({
        title: "Still loading",
        description: "Template data is still loading. Please try again in a moment.",
      });
      return;
    }
    setTemplateCheckOpen(true);
    setTemplateCheckRunning(true);
    const tagById = new Map((tags || []).map((t) => [String(t._id), t]));

    const sectionMeta = sections.map((section) => {
      const selectedTagIds = new Set((section.tagIds || []).map((id) => String(id)));
      const taggedRules = rules
        .filter((r) => r.tagId && selectedTagIds.has(String(r.tagId)))
        .map((r) => {
          const tagName = (r.tagId && tagById.get(String(r.tagId))?.name) || "Untagged";
          return { tagName, content: r.content };
        });
      const payload: ConflictCheckPayload = {
        sectionName: section.name,
        description: section.description,
        examples: section.examples,
        adhereToFormatting: section.adhereToFormatting ?? false,
        formatTemplate: section.adhereToFormatting ? section.formatTemplate : undefined,
        taggedRules,
      };
      const hash = hashConflictPayload(payload);
      const cached = force ? undefined : getCachedConflict(String(section._id), hash);
      return { section, payload, hash, cached };
    });

    const initial: TemplateConflictEntry[] = sectionMeta.map(({ section, cached }) => {
      if (cached) {
        const data = cached.result;
        return {
          sectionId: String(section._id),
          sectionName: section.name,
          status: data.hasConflicts && data.conflicts.length > 0 ? "conflict" : "ok",
          result: data,
        };
      }
      return {
        sectionId: String(section._id),
        sectionName: section.name,
        status: "pending",
      };
    });
    setTemplateCheckResults(initial);

    const finalResults: TemplateConflictEntry[] = [...initial];
    await Promise.all(
      sectionMeta.map(async ({ section, payload, hash, cached }, idx) => {
        if (cached) return;
        try {
          const res = await apiRequest(
            "POST",
            "/api/templates/sections/check-conflicts",
            payload,
          );
          const data = (await res.json()) as SectionConflictResult;
          setCachedConflict(String(section._id), { hash, result: data });
          const updated: TemplateConflictEntry = {
            ...initial[idx],
            status: data.hasConflicts && data.conflicts.length > 0 ? "conflict" : "ok",
            result: data,
          };
          finalResults[idx] = updated;
          setTemplateCheckResults((prev) => {
            const next = [...prev];
            next[idx] = updated;
            return next;
          });
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : "Check failed";
          const updated: TemplateConflictEntry = {
            ...initial[idx],
            status: "error",
            error: message,
          };
          finalResults[idx] = updated;
          setTemplateCheckResults((prev) => {
            const next = [...prev];
            next[idx] = updated;
            return next;
          });
        }
      }),
    );
    setTemplateCheckRunning(false);

    if (onConflictCheckComplete) {
      const sectionsWithConflicts = finalResults.filter((r) => r.status === "conflict").length;
      const conflictCount = finalResults.reduce(
        (sum, r) => sum + (r.result?.conflicts.length || 0),
        0,
      );
      const errorCount = finalResults.filter((r) => r.status === "error").length;
      onConflictCheckComplete(template._id, {
        checkedAt: Date.now(),
        totalSections: finalResults.length,
        sectionsWithConflicts,
        conflictCount,
        errorCount,
      });
    }
  };

  const handleRenameSave = async () => {
    if (nameValue.trim() && nameValue.trim() !== template.name) {
      try {
        await updateTemplateMutation({ id: template._id, userId, name: nameValue.trim() });
        toast({ title: "Template renamed" });
      } catch {
        toast({ title: "Error", description: "Failed to rename template.", variant: "destructive" });
      }
    }
    setEditingName(false);
  };

  const scrollToSection = (sectionId: string) => {
    const el = sectionRefs.current.get(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSectionId(sectionId);
    }
  };

  const hasUnsavedSectionData = newSectionName.trim() !== "" || newSectionDesc.trim() !== "" || newSectionExamples.length > 0 || newExampleText.trim() !== "";

  const clearAndCloseAddSection = () => {
    setShowAddSection(false);
    setNewSectionName("");
    setNewSectionDesc("");
    setNewSectionExamples([]);
    setNewExampleText("");
  };

  useEffect(() => {
    if (shouldScrollToLast && lastSectionRef.current) {
      lastSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setShouldScrollToLast(false);
    }
  }, [shouldScrollToLast, sections]);

  const handleAddSection = async () => {
    if (!newSectionName.trim() || !newSectionDesc.trim()) return;
    try {
      await addSectionMutation({
        templateId: template._id,
        userId,
        name: newSectionName,
        description: newSectionDesc,
        examples: newSectionExamples.length > 0 ? newSectionExamples : undefined,
      });
      setNewSectionName("");
      setNewSectionDesc("");
      setNewSectionExamples([]);
      setNewExampleText("");
      setShowAddSection(false);
      setShouldScrollToLast(true);
      toast({ title: "Section added" });
    } catch {
      toast({ title: "Error", description: "Failed to add section.", variant: "destructive" });
    }
  };

  const handleUpdateSection = async (
    sectionId: Id<"templateSections">,
    updates: SectionUpdates,
  ) => {
    try {
      await updateSectionMutation({
        id: sectionId,
        userId,
        ...updates,
      });
      toast({ title: "Section updated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update section.", variant: "destructive" });
      throw error;
    }
  };

  const handleDeleteSection = async (sectionId: Id<"templateSections">) => {
    try {
      await deleteSectionMutation({ id: sectionId, userId });
      toast({ title: "Section deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete section.", variant: "destructive" });
    }
  };

  const handleMoveSection = async (index: number, direction: "up" | "down") => {
    if (!sections) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newOrder = [...sections];
    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(newIndex, 0, moved);

    try {
      await reorderSectionsMutation({
        templateId: template._id,
        userId,
        sectionIds: newOrder.map((s) => s._id),
      });
    } catch {
      toast({ title: "Error", description: "Failed to reorder sections.", variant: "destructive" });
    }
  };

  if (!sections) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-border bg-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            {editingName ? (
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRenameSave();
                  if (e.key === "Escape") { setNameValue(template.name); setEditingName(false); }
                }}
                onBlur={handleRenameSave}
                className="text-2xl font-heading font-bold text-foreground bg-background border border-primary/30 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-primary w-full max-w-md"
                data-testid="input-rename-template-editor"
              />
            ) : (
              <h1
                className="text-2xl font-heading font-bold text-foreground flex items-center gap-2 group/title cursor-pointer"
                onClick={() => { setEditingName(true); setNameValue(template.name); }}
                data-testid="text-template-name"
              >
                {template.name}
                <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity" />
              </h1>
            )}
            {template.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {template.description}
              </p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <UserRound className="w-4 h-4 text-muted-foreground" />
              <Select
                value={template.profileId ? String(template.profileId) : "none"}
                onValueChange={async (value) => {
                  try {
                    await updateTemplateMutation({
                      id: template._id,
                      userId,
                      profileId:
                        value === "none" ? null : (value as Id<"profiles">),
                    });
                    toast({
                      title: value === "none" ? "Profile removed" : "Profile assigned",
                      description:
                        value === "none"
                          ? "This template no longer uses a profile."
                          : "The profile's prompt will be applied to every section generated from this template.",
                    });
                  } catch {
                    toast({
                      title: "Error",
                      description: "Failed to update the template's profile.",
                      variant: "destructive",
                    });
                  }
                }}
              >
                <SelectTrigger
                  className="h-8 w-[180px] text-xs"
                  data-testid="select-template-profile"
                  title="Assign a Profile whose prompt is included whenever sections are generated from this template"
                >
                  <SelectValue placeholder="Profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="option-profile-none">
                    No profile
                  </SelectItem>
                  {(profiles || []).map((p) => (
                    <SelectItem
                      key={String(p._id)}
                      value={String(p._id)}
                      data-testid={`option-profile-${String(p._id)}`}
                    >
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => runTemplateConflictCheck(false)}
              disabled={templateCheckRunning || !sections || sections.length === 0 || !rules}
              className="gap-2"
              title="Run conflict checks for every section in this template (uses cached results when sections haven't changed)"
              data-testid="button-check-template-conflicts"
            >
              {templateCheckRunning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              Check all conflicts
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={templateCheckOpen} onOpenChange={setTemplateCheckOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] flex flex-col"
          data-testid="dialog-template-conflicts"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Template conflict check
              {templateCheckRunning && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const total = templateCheckResults.length;
            const done = templateCheckResults.filter((r) => r.status !== "pending").length;
            const conflictCount = templateCheckResults.reduce(
              (sum, r) => sum + (r.result?.conflicts.length || 0),
              0,
            );
            const sectionsWithConflicts = templateCheckResults.filter(
              (r) => r.status === "conflict",
            ).length;
            const errorCount = templateCheckResults.filter((r) => r.status === "error").length;
            return (
              <div className="text-sm text-muted-foreground" data-testid="text-template-conflicts-summary">
                {templateCheckRunning
                  ? `Analyzing ${done} of ${total} section${total === 1 ? "" : "s"}…`
                  : total === 0
                    ? "No sections to check."
                    : conflictCount === 0 && errorCount === 0
                      ? `No conflicts found across ${total} section${total === 1 ? "" : "s"}.`
                      : `${conflictCount} potential conflict${conflictCount === 1 ? "" : "s"} across ${sectionsWithConflicts} section${sectionsWithConflicts === 1 ? "" : "s"}${errorCount > 0 ? ` · ${errorCount} failed` : ""}.`}
              </div>
            );
          })()}
          <ScrollArea className="flex-1 -mx-6 px-6">
            <ul className="space-y-3">
              {templateCheckResults.map((entry) => (
                <li
                  key={entry.sectionId}
                  className="border border-border rounded p-3"
                  data-testid={`template-conflict-section-${entry.sectionId}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        setTemplateCheckOpen(false);
                        scrollToSection(entry.sectionId);
                      }}
                      className="font-medium text-sm text-left hover:underline"
                      data-testid={`button-jump-to-section-${entry.sectionId}`}
                    >
                      {entry.sectionName}
                    </button>
                    <div className="text-xs">
                      {entry.status === "pending" && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Checking…
                        </span>
                      )}
                      {entry.status === "ok" && (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" />
                          No conflicts
                        </span>
                      )}
                      {entry.status === "conflict" && (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <AlertTriangle className="w-3 h-3" />
                          {entry.result?.conflicts.length}{" "}
                          conflict
                          {entry.result?.conflicts.length === 1 ? "" : "s"}
                        </span>
                      )}
                      {entry.status === "error" && (
                        <span className="inline-flex items-center gap-1 text-destructive">
                          <AlertTriangle className="w-3 h-3" />
                          Failed
                        </span>
                      )}
                    </div>
                  </div>
                  {entry.status === "conflict" && entry.result && (
                    <ul className="space-y-2">
                      {entry.result.conflicts.map((c, i) => (
                        <li
                          key={i}
                          className="text-sm border border-border rounded p-2 bg-muted/30"
                          data-testid={`template-conflict-item-${entry.sectionId}-${i}`}
                        >
                          {c.between && c.between.length > 0 && (
                            <div className="text-xs text-muted-foreground mb-1">
                              Between: {c.between.join(" ↔ ")}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap">{c.explanation}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {entry.status === "error" && entry.error && (
                    <div className="text-xs text-destructive">{entry.error}</div>
                  )}
                </li>
              ))}
            </ul>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => runTemplateConflictCheck(true)}
              disabled={templateCheckRunning}
              data-testid="button-rerun-template-conflicts"
            >
              {templateCheckRunning ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Re-run
            </Button>
            <Button
              size="sm"
              onClick={() => setTemplateCheckOpen(false)}
              data-testid="button-close-template-conflicts"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-48 shrink-0 border-r border-border bg-muted/30 flex flex-col">
          <div className="px-3 py-3 border-b border-border">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Sections ({sections.length})
            </h2>
          </div>
          <ScrollArea className="flex-1">
            <div className="py-1">
              {sections.map((section, index) => (
                <button
                  key={section._id}
                  onClick={() => scrollToSection(section._id)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-muted ${
                    activeSectionId === section._id
                      ? "bg-primary/10 text-primary font-medium border-r-2 border-primary"
                      : "text-foreground"
                  }`}
                  data-testid={`button-toc-section-${section._id}`}
                >
                  <span className="text-xs text-muted-foreground mr-1.5">{index + 1}.</span>
                  <span className="truncate">{section.name}</span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-6 pt-4 pb-6 max-w-2xl">
            {sections.map((section, index) => (
              <div
                key={section._id}
                ref={(el) => {
                  if (el) {
                    sectionRefs.current.set(section._id, el);
                  } else {
                    sectionRefs.current.delete(section._id);
                  }
                  if (index === sections.length - 1 && el) {
                    lastSectionRef.current = el;
                  }
                }}
              >
                <SectionEditor
                  section={section}
                  userId={userId}
                  onUpdate={(updates) => handleUpdateSection(section._id, updates)}
                  onDelete={() => handleDeleteSection(section._id)}
                  canDelete={sections.length > 1}
                  tags={tags || []}
                  rules={rules || []}
                  siblingSections={sections.filter((s) => s._id !== section._id)}
                  onMoveUp={() => handleMoveSection(index, "up")}
                  onMoveDown={() => handleMoveSection(index, "down")}
                  isFirst={index === 0}
                  isLast={index === sections.length - 1}
                  showReorder={sections.length > 1}
                  getCachedConflict={getCachedConflict}
                  setCachedConflict={setCachedConflict}
                />
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      <div className="border-t border-border bg-card p-4 flex justify-center">
        <Dialog open={showAddSection} onOpenChange={(open) => {
          if (!open && hasUnsavedSectionData) {
            setShowDiscardConfirm(true);
            return;
          }
          setShowAddSection(open);
          if (!open) {
            clearAndCloseAddSection();
          }
        }}>
          <DialogTrigger asChild>
            <Button
              className="gap-2"
              data-testid="button-add-section"
            >
              <Plus className="w-4 h-4" />
              Add Section
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Add New Section</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-1">
              <div className="space-y-2">
                <Label htmlFor="newSectionName">
                  Section Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="newSectionName"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="e.g., Chief Complaint"
                  required
                  data-testid="input-new-section-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newSectionDesc">
                  LLM Prompt (Description) <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="newSectionDesc"
                  value={newSectionDesc}
                  onChange={(e) => setNewSectionDesc(e.target.value)}
                  placeholder="Describe what the LLM should generate for this section..."
                  required
                  data-testid="input-new-section-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Examples (Optional)</Label>
                <div className="space-y-2">
                  {newSectionExamples.map((example, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 bg-muted/30 p-2 rounded text-sm"
                    >
                      <span className="flex-1 whitespace-pre-wrap">{example}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 shrink-0"
                        onClick={() => setNewSectionExamples(newSectionExamples.filter((_, i) => i !== index))}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Textarea
                    value={newExampleText}
                    onChange={(e) => setNewExampleText(e.target.value)}
                    placeholder="Add an example..."
                    className="text-sm min-h-[60px]"
                    data-testid="input-new-section-example"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (newExampleText.trim()) {
                        setNewSectionExamples([...newSectionExamples, newExampleText.trim()]);
                        setNewExampleText("");
                      }
                    }}
                    disabled={!newExampleText.trim()}
                    data-testid="button-add-section-example"
                  >
                    Add Example
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-border shrink-0">
              <Button
                variant="ghost"
                onClick={() => {
                  if (hasUnsavedSectionData) {
                    setShowDiscardConfirm(true);
                  } else {
                    clearAndCloseAddSection();
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddSection}
                disabled={!newSectionName.trim() || !newSectionDesc.trim()}
                data-testid="button-confirm-add-section"
              >
                Add Section
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved data in the new section form. Do you want to discard your changes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-keep-editing">Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearAndCloseAddSection}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-discard-section"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Templates() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const [matchTemplateRoute, templateRouteParams] = useRoute<{ id: string }>("/templates/:id");
  const rawRouteTemplateId = matchTemplateRoute ? templateRouteParams?.id ?? null : null;
  const routeTemplateId =
    rawRouteTemplateId && /^[a-z0-9_]{16,}$/i.test(rawRouteTemplateId)
      ? rawRouteTemplateId
      : null;
  const selectedTemplateId = (routeTemplateId as Id<"templates"> | null) ?? null;
  const setSelectedTemplateId = (id: Id<"templates"> | null) => {
    if (id) {
      navigate(`/templates/${id}`);
    } else {
      navigate("/templates");
    }
  };
  const [userSynced, setUserSynced] = useState(false);
  const [renamingTemplateId, setRenamingTemplateId] = useState<Id<"templates"> | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [conflictSummaries, setConflictSummaries] = useState<
    Record<string, TemplateConflictSummary>
  >({});
  const { toast } = useToast();

  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const deleteTemplate = useMutation(api.templates.remove);
  const cloneTemplate = useMutation(api.templates.clone);
  const updateTemplate = useMutation(api.templates.update);
  const reorderTemplatesMutation = useMutation(api.templates.reorderTemplates);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      })
        .then(() => {
          setUserSynced(true);
        })
        .catch((err) => {
          console.error("Failed to sync user with Convex:", err);
        });
    }
  }, [user, userSynced, getOrCreateUser]);

  const convexUser = useQuery(
    api.users.getCurrentUser,
    user && userSynced ? { googleId: user.id } : "skip"
  );
  const isAdmin = useIsAdmin();
  const templates = useQuery(
    api.templates.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );

  const selectedTemplate = useQuery(
    api.templates.get,
    selectedTemplateId && convexUser
      ? { id: selectedTemplateId, userId: convexUser._id }
      : "skip"
  );

  const handleDeleteTemplate = async (id: Id<"templates">) => {
    if (!convexUser) return;
    try {
      await deleteTemplate({ id, userId: convexUser._id });
      if (selectedTemplateId === id) {
        setSelectedTemplateId(null);
      }
      setConflictSummaries((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast({ title: "Template deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" });
    }
  };

  const handleCloneTemplate = async (id: Id<"templates">) => {
    if (!convexUser) return;
    try {
      const newId = await cloneTemplate({ id, userId: convexUser._id });
      setSelectedTemplateId(newId);
      toast({ title: "Template cloned" });
    } catch {
      toast({ title: "Error", description: "Failed to clone template.", variant: "destructive" });
    }
  };

  const handleMoveTemplate = async (templateId: Id<"templates">, direction: "up" | "down") => {
    if (!convexUser || !templates) return;
    const currentIndex = templates.findIndex((t) => t._id === templateId);
    if (currentIndex === -1) return;
    if (direction === "up" && currentIndex === 0) return;
    if (direction === "down" && currentIndex === templates.length - 1) return;

    const newOrder = [...templates];
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    [newOrder[currentIndex], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[currentIndex]];

    try {
      await reorderTemplatesMutation({
        userId: convexUser._id,
        templateIds: newOrder.map((t) => t._id),
      });
    } catch {
      toast({ title: "Error", description: "Failed to reorder templates.", variant: "destructive" });
    }
  };

  const handleRenameTemplate = async (templateId: Id<"templates">, newName: string) => {
    if (!convexUser || !newName.trim()) {
      setRenamingTemplateId(null);
      return;
    }
    try {
      await updateTemplate({ id: templateId, userId: convexUser._id, name: newName.trim() });
      toast({ title: "Template renamed" });
    } catch {
      toast({ title: "Error", description: "Failed to rename template.", variant: "destructive" });
    }
    setRenamingTemplateId(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    window.location.href = "/api/login";
    return null;
  }

  if (!userSynced || !convexUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Templates — LRScribe" noindex />
      {/* Top Header */}
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-primary transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/profiles"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-profiles"
          >
            <UserRound className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Profiles</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Pane - Template List */}
        <aside className="w-80 border-r border-border bg-card flex flex-col shrink-0">
          <div className="p-4 border-b border-border">
            <NewTemplateDialog userId={convexUser._id} onSuccess={() => {}} />
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2">
              {templates === undefined ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No templates yet. Create your first template to get
                    started.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {templates.map((template) => (
                    <div
                      key={template._id}
                      className={`w-full text-left p-3 rounded-lg transition-colors group relative ${
                        selectedTemplateId === template._id
                          ? "bg-primary/10 border border-primary/20"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      {renamingTemplateId === template._id ? (
                        <div className="pr-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameTemplate(template._id, renameValue);
                              if (e.key === "Escape") setRenamingTemplateId(null);
                            }}
                            onBlur={() => handleRenameTemplate(template._id, renameValue)}
                            className="w-full text-sm font-medium bg-background border border-primary/30 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
                            data-testid={`input-rename-template-${template._id}`}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedTemplateId(template._id)}
                          className="w-full text-left pr-8"
                          data-testid={`button-template-${template._id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <h3
                                className={`font-medium text-sm truncate ${
                                  selectedTemplateId === template._id
                                    ? "text-primary"
                                    : "text-foreground"
                                }`}
                              >
                                {template.name}
                              </h3>
                              {template.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                  {template.description}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                Updated{" "}
                                {format(new Date(template.updatedAt), "MMM d, yyyy")}
                              </p>
                              {(() => {
                                const summary = conflictSummaries[template._id];
                                if (!summary) return null;
                                if (summary.sectionsWithConflicts > 0) {
                                  return (
                                    <div
                                      className="mt-1.5 inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
                                      title={`${summary.conflictCount} potential conflict${summary.conflictCount === 1 ? "" : "s"} across ${summary.sectionsWithConflicts} section${summary.sectionsWithConflicts === 1 ? "" : "s"}`}
                                      data-testid={`badge-template-conflicts-${template._id}`}
                                    >
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>
                                        {summary.conflictCount} conflict
                                        {summary.conflictCount === 1 ? "" : "s"} in{" "}
                                        {summary.sectionsWithConflicts} section
                                        {summary.sectionsWithConflicts === 1 ? "" : "s"}
                                      </span>
                                    </div>
                                  );
                                }
                                if (summary.errorCount > 0) {
                                  return (
                                    <div
                                      className="mt-1.5 inline-flex items-center gap-1 text-xs text-destructive"
                                      title={`${summary.errorCount} section${summary.errorCount === 1 ? "" : "s"} failed to check`}
                                      data-testid={`badge-template-conflict-errors-${template._id}`}
                                    >
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>
                                        Check failed for {summary.errorCount} section
                                        {summary.errorCount === 1 ? "" : "s"}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    className="mt-1.5 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"
                                    title={`No conflicts found across ${summary.totalSections} section${summary.totalSections === 1 ? "" : "s"}`}
                                    data-testid={`badge-template-no-conflicts-${template._id}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>No conflicts</span>
                                  </div>
                                );
                              })()}
                            </div>
                            <ChevronRight
                              className={`w-4 h-4 text-muted-foreground transition-transform ${
                                selectedTemplateId === template._id
                                  ? "translate-x-0.5"
                                  : ""
                              }`}
                            />
                          </div>
                        </button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`button-template-menu-${template._id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {templates && templates.indexOf(template) > 0 && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveTemplate(template._id, "up");
                              }}
                              data-testid={`button-move-up-template-${template._id}`}
                            >
                              <ChevronUp className="w-4 h-4 mr-2" />
                              Move Up
                            </DropdownMenuItem>
                          )}
                          {templates && templates.indexOf(template) < templates.length - 1 && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveTemplate(template._id, "down");
                              }}
                              data-testid={`button-move-down-template-${template._id}`}
                            >
                              <ChevronDown className="w-4 h-4 mr-2" />
                              Move Down
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingTemplateId(template._id);
                              setRenameValue(template.name);
                            }}
                            data-testid={`button-rename-template-${template._id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneTemplate(template._id);
                            }}
                            data-testid={`button-clone-template-${template._id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Clone
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template._id);
                            }}
                            data-testid={`button-delete-template-${template._id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Center Pane - Template Editor */}
        <main className="flex-1 overflow-hidden bg-background">
          {selectedTemplate ? (
            <TemplateEditor
              template={selectedTemplate}
              userId={convexUser._id}
              onBack={() => setSelectedTemplateId(null)}
              onConflictCheckComplete={(templateId, summary) =>
                setConflictSummaries((prev) => ({ ...prev, [templateId]: summary }))
              }
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Select a Template
              </h2>
              <p className="text-muted-foreground max-w-md">
                Choose a template from the list on the left to edit its
                sections, or create a new template to get started.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
